import _extends from "@babel/runtime/helpers/esm/extends";
import React, { Fragment, useMemo, useRef, useImperativeHandle } from 'react';
import DragDrop from '../DragDrop';
import Pagination from '../Pagination';
import Spinner from '../Spinner';
import StackView from '../StackView';
import Summary from '../Summary';
import { useCss } from '../system';
import { useId } from '../hooks';
import { reorderArray } from '../utils';
import { BodyRows, HeaderCell } from './components';
import { CollapsibleRowsContext, useCollapsibleRows, useColumnSort, useColumnWidths, useScrollShadows, useScrollSync, useVariants } from './hooks';
import { getParsedColumns } from './utils';
var defaultAccessibility = {
  toggleAllSubRowsTooltipTitle: function toggleAllSubRowsTooltipTitle(allSubRowsOpen) {
    return allSubRowsOpen ? 'Close all sub rows' : 'Open all sub rows';
  }
};
var defaultHiddenColumnIds = [];

var _ref3 = /*#__PURE__*/React.createElement(Spinner, {
  size: "xl"
});

var DataTable = function DataTable(props) {
  var _props$accessibility = props.accessibility,
      accessibility = _props$accessibility === void 0 ? defaultAccessibility : _props$accessibility,
      _props$canCollapseSub = props.canCollapseSubRows,
      canCollapseSubRows = _props$canCollapseSub === void 0 ? true : _props$canCollapseSub,
      columns = props.columns,
      currentPage = props.currentPage,
      data = props.data,
      dataTableRef = props.dataTableRef,
      defaultSort = props.defaultSort,
      defaultAllSubRowsOpen = props.defaultAllSubRowsOpen,
      droppableId = props.droppableId,
      empty = props.empty,
      _props$getRowId = props.getRowId,
      getRowId = _props$getRowId === void 0 ? function (rowData, rowIndex) {
    return rowData.id || (droppableId === undefined ? rowIndex : droppableId + "-" + rowIndex);
  } : _props$getRowId,
      getRowLink = props.getRowLink,
      getSubData = props.getSubData,
      _props$hiddenColumnId = props.hiddenColumnIds,
      hiddenColumnIds = _props$hiddenColumnId === void 0 ? defaultHiddenColumnIds : _props$hiddenColumnId,
      keyboardShortcuts = props.keyboardShortcuts,
      loading = props.loading,
      loadingTitle = props.loadingTitle,
      onColumnSort = props.onColumnSort,
      onDragStart = props.onDragStart,
      onDragEnd = props.onDragEnd,
      onDrop = props.onDrop,
      onPageChange = props.onPageChange,
      onRowClick = props.onRowClick,
      onRowSort = props.onRowSort,
      sourceOnly = props.sourceOnly,
      totalPages = props.totalPages,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? {} : _props$variant;
  var css = useCss();
  var tableId = useId('table');
  var hasParentDragDrop = DragDrop.useDragDrop();
  var isLoadingOrEmpty = loading || data.length <= 0;
  var headerRef = useRef();
  var bodyRef = useRef();
  var footerRef = useRef();
  var scrollRefs = [headerRef, bodyRef];
  var draggable = sourceOnly || onRowSort || onDrop;
  var getVariant = useVariants(variant);
  var collapsibleRows = useCollapsibleRows(data, defaultAllSubRowsOpen);
  var hasSubRows = Boolean(canCollapseSubRows && getSubData);
  var parsedColumns = useMemo(function () {
    return getParsedColumns({
      accessibility: accessibility,
      hasSubRows: hasSubRows,
      columns: columns,
      hiddenColumnIds: hiddenColumnIds,
      draggable: draggable
    });
  }, [columns, hasSubRows, hiddenColumnIds, draggable]);
  var hasFooterColumns = parsedColumns.map(Object.keys).some(function (keys) {
    return keys.some(function (key) {
      return key === 'footer';
    });
  });

  var _useColumnWidths = useColumnWidths({
    columns: parsedColumns,
    data: data,
    loading: loading,
    bodyRef: bodyRef
  }),
      columnWidths = _useColumnWidths.columnWidths,
      getColumnRef = _useColumnWidths.getColumnRef;

  var columnSort = useColumnSort({
    defaultSort: defaultSort,
    onColumnSort: onColumnSort
  });
  var shadowsToRender = useScrollShadows(bodyRef);
  var bodyRowsProps = {
    columns: parsedColumns,
    collapsibleRows: collapsibleRows,
    columnWidths: columnWidths,
    data: data,
    draggable: draggable,
    getColumnRef: getColumnRef,
    getRowId: getRowId,
    getRowLink: getRowLink,
    getSubData: getSubData,
    getVariant: getVariant,
    keyboardShortcuts: keyboardShortcuts,
    onRowClick: onRowClick
  };
  var bodyToRender;

  if (hasFooterColumns) {
    scrollRefs.push(footerRef);
  }

  useScrollSync.apply(void 0, scrollRefs);
  useImperativeHandle(dataTableRef, function () {
    return collapsibleRows;
  });

  if (hasParentDragDrop || draggable) {
    bodyToRender = /*#__PURE__*/React.createElement(DragDrop.Provider, {
      onDragEnd: function onDragEnd(_ref) {
        var source = _ref.source,
            destination = _ref.destination;

        if (onRowSort && destination) {
          onRowSort(reorderArray({
            list: data,
            startIndex: source.index,
            endIndex: destination.index
          }));
        }
      }
    }, /*#__PURE__*/React.createElement(DragDrop.Droppable, {
      droppableId: droppableId === undefined ? tableId : droppableId,
      sourceOnly: sourceOnly,
      onDragStart: onDragStart,
      onDragEnd: onDragEnd,
      onDrop: onDrop
    }, function (provided) {
      return /*#__PURE__*/React.createElement("div", _extends({
        ref: function ref(_ref2) {
          bodyRef.current = _ref2;
          provided.innerRef(_ref2);
        },
        role: "rowgroup",
        className: css(getVariant('body'))
      }, provided.droppableProps), /*#__PURE__*/React.createElement(BodyRows, bodyRowsProps), provided.placeholder);
    }));
  } else {
    bodyToRender = /*#__PURE__*/React.createElement("div", {
      ref: bodyRef,
      role: "rowgroup",
      className: css(getVariant('body'))
    }, /*#__PURE__*/React.createElement(BodyRows, bodyRowsProps));
  }

  return /*#__PURE__*/React.createElement(Fragment, null, isLoadingOrEmpty && /*#__PURE__*/React.createElement(StackView, {
    grow: 1,
    alignment: "center",
    distribution: "center",
    minHeight: "100%",
    paddingVertical: 8
  }, loading ? /*#__PURE__*/React.createElement(Summary, {
    image: _ref3,
    title: loadingTitle
  }) : empty), /*#__PURE__*/React.createElement(CollapsibleRowsContext.Provider, {
    value: collapsibleRows
  }, /*#__PURE__*/React.createElement("div", {
    role: getRowLink ? undefined : 'grid',
    style: isLoadingOrEmpty ? {
      display: 'none'
    } : undefined,
    className: "uikit-reset " + css({
      position: 'relative'
    })
  }, /*#__PURE__*/React.createElement("div", {
    ref: headerRef,
    role: "rowgroup",
    className: css(getVariant('header'))
  }, /*#__PURE__*/React.createElement("div", {
    role: "row",
    className: css(getVariant('headerRow'))
  }, parsedColumns.map(function (column, columnIndex) {
    var cellVariantClassName = getVariant('headerCell', {
      columnIndex: columnIndex,
      isSortable: column.sortable
    });
    return /*#__PURE__*/React.createElement(HeaderCell, {
      key: columnIndex,
      cellRef: getColumnRef(columnIndex + ".0"),
      column: column,
      columnIndex: columnIndex,
      columnSort: columnSort,
      className: "uikit-reset " + css([].concat(cellVariantClassName, [column.css]))
    });
  }))), bodyToRender, hasFooterColumns && /*#__PURE__*/React.createElement("div", {
    ref: footerRef,
    role: "rowgroup",
    className: css(getVariant('footer'))
  }, /*#__PURE__*/React.createElement("div", {
    role: "row",
    className: css(getVariant('footerRow'))
  }, parsedColumns.map(function (column, columnIndex) {
    var cellVariantClassName = getVariant('footerCell', {
      columnIndex: columnIndex,
      isSortable: column.sortable
    });
    return /*#__PURE__*/React.createElement("div", {
      key: columnIndex,
      role: "cell",
      ref: getColumnRef(columnIndex + "." + (data.length + 1)),
      className: "uikit-reset " + css([].concat(cellVariantClassName, [column.css]))
    }, typeof column.footer === 'string' || /*#__PURE__*/React.isValidElement(column.footer) ? column.footer : column.footer ? /*#__PURE__*/React.createElement(column.footer, {
      columnIndex: columnIndex
    }) : null);
  }))), shadowsToRender, /*#__PURE__*/React.createElement(Pagination, {
    currentPage: currentPage,
    totalPages: totalPages,
    onPageChange: onPageChange,
    position: "sticky",
    bottom: 0,
    zIndex: 35,
    marginTop: "-1px",
    backgroundColor: "white",
    borderTopWidth: 1,
    borderTopColor: "grey-3",
    className: css({
      '@media print': {
        position: 'relative'
      }
    })
  }))));
};

export default DataTable;